import * as Styled from './FanNotesModal.styles';

import { FC, useState } from 'react';
import {
	useCreateFanNote,
	useDeleteFanNote,
	useFanNotes,
	usePartnerFanId,
	useUpdateFanNote,
} from '../../hooks/useFanNotes';

import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupModal } from 'features/common/components/Punchup/composites/dialogs/PunchupModal';

interface FanNotesModalProps {
	userId: number;
	fanName: string;
	onClose: () => void;
}

export const FanNotesModal: FC<FanNotesModalProps> = ({ userId, fanName, onClose }) => {
	const [newNoteText, setNewNoteText] = useState('');
	const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
	const [editingText, setEditingText] = useState('');

	// The fan-note hooks key on partnerFanId, not the raw userId — resolve it first (mirrors the
	// green AttendeeActions FanNotesModal). Passing userId would hit /partners/fans-notes/:id with
	// the wrong id.
	const partnerFanId = usePartnerFanId(userId);

	const { data: notes = [], isLoading } = useFanNotes(partnerFanId);
	// A disabled react-query (enabled:false while partnerFanId resolves) reports isLoading:true; guard it.
	const notesLoading = !!partnerFanId && isLoading;

	const { createNote, isLoading: creating } = useCreateFanNote(partnerFanId, {
		onSuccess: () => setNewNoteText(''),
	});

	const { updateNote, isLoading: updating } = useUpdateFanNote(partnerFanId, {
		onSuccess: () => setEditingNoteId(null),
	});

	const { deleteNote, isLoading: deleting } = useDeleteFanNote(partnerFanId);

	const handleCreate = () => {
		const trimmed = newNoteText.trim();
		if (trimmed) createNote(trimmed);
	};

	const handleEditStart = (noteId: number, currentText: string) => {
		setEditingNoteId(noteId);
		setEditingText(currentText);
	};

	const handleEditSave = () => {
		const trimmed = editingText.trim();
		if (editingNoteId !== null && trimmed) {
			updateNote({ noteId: editingNoteId, note: trimmed });
		}
	};

	const handleEditCancel = () => {
		setEditingNoteId(null);
		setEditingText('');
	};

	return (
		<PunchupModal title={`Notes — ${fanName}`}>
			{notesLoading ? (
				<Styled.EmptyState>Loading...</Styled.EmptyState>
			) : notes.length === 0 ? (
				<Styled.EmptyState>No notes yet for this fan.</Styled.EmptyState>
			) : (
				<Styled.NotesList>
					{notes.map((note) => (
						<Styled.NoteItem key={note.id}>
							{editingNoteId === note.id ? (
								<>
									<Styled.EditTextarea value={editingText} onChange={(e) => setEditingText(e.target.value)} autoFocus />
									<Styled.NoteActions>
										<PunchupButton
											variant="primary"
											text={updating ? 'Saving...' : 'Save'}
											onClick={handleEditSave}
											disabled={updating || !editingText.trim()}
										/>
										<PunchupButton variant="secondary" text="Cancel" onClick={handleEditCancel} disabled={updating} />
									</Styled.NoteActions>
								</>
							) : (
								<>
									<Styled.NoteMeta>
										{new Date(note.updated_at).toLocaleDateString('en-US', {
											year: 'numeric',
											month: 'short',
											day: 'numeric',
										})}
									</Styled.NoteMeta>
									<Styled.NoteText>{note.note}</Styled.NoteText>
									<Styled.NoteActions>
										<Styled.NoteActionButton onClick={() => handleEditStart(note.id, note.note)}>
											Edit
										</Styled.NoteActionButton>
										<Styled.NoteActionButton className="danger" onClick={() => deleteNote(note.id)} disabled={deleting}>
											Delete
										</Styled.NoteActionButton>
									</Styled.NoteActions>
								</>
							)}
						</Styled.NoteItem>
					))}
				</Styled.NotesList>
			)}

			<Styled.AddSection>
				<Styled.Label htmlFor="new-fan-note">Add a note</Styled.Label>
				<Styled.EditTextarea
					id="new-fan-note"
					placeholder="Nut allergy, VIP, wheelchair access…"
					value={newNoteText}
					onChange={(e) => setNewNoteText(e.target.value)}
				/>
				<div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
					<PunchupButton variant="secondary" text="Close" onClick={onClose} disabled={creating} />
					<PunchupButton
						variant="primary"
						text={creating ? 'Adding...' : 'Add Note'}
						onClick={handleCreate}
						disabled={creating || !newNoteText.trim()}
					/>
				</div>
			</Styled.AddSection>
		</PunchupModal>
	);
};
